import { ErrorCode, FileRejection } from 'react-dropzone';
import { DOCUMENT_UPLOAD_STATUS_ID, PresignedUploadModel } from '@/lib/models';

export async function uploadFile<T extends PresignedUploadModel>({
	file,
	presignedUploadGetter,
	uploadStatusUpdater,
	onProgress,
}: {
	file: File;
	presignedUploadGetter: () => Promise<{ upload: T }>;
	uploadStatusUpdater: (upload: T, uploadStatusId: DOCUMENT_UPLOAD_STATUS_ID) => Promise<void>;
	onProgress?: (percent: number) => void;
}) {
	const { upload } = await presignedUploadGetter();
	await uploadStatusUpdater(upload, DOCUMENT_UPLOAD_STATUS_ID.IN_PROGRESS);

	const finishedUpload = await new Promise((resolve: (presignedUpload: T) => void, reject) => {
		const xhr = new XMLHttpRequest();

		xhr.upload.addEventListener('progress', (event) => {
			if (event.lengthComputable) {
				const percentCompleted = Math.round((event.loaded * 100) / event.total);
				onProgress?.(percentCompleted);
			}
		});

		xhr.addEventListener('load', () => resolve(upload));
		xhr.addEventListener('error', () =>
			reject({ code: 'UPLOAD_ERROR', message: 'There was an error uploading your document.' })
		);
		xhr.addEventListener('abort', () =>
			reject({ code: 'UPLOAD_ABORTED', message: 'The document upload was aborted.' })
		);
		xhr.open(upload.httpMethod, upload.url, true);

		for (const [headerName, headerValue] of Object.entries(upload.httpHeaders)) {
			xhr.setRequestHeader(headerName, headerValue);
		}

		xhr.send(file);
	});

	await uploadStatusUpdater(finishedUpload, DOCUMENT_UPLOAD_STATUS_ID.COMPLETE);
	return finishedUpload;
}

export const getFileDropzoneRejectionMessage = (fileRejections: FileRejection[]) => {
	let message = '';

	for (const fileRejection of fileRejections) {
		for (const rejectionError of fileRejection.errors) {
			if (rejectionError.code === ErrorCode.FileInvalidType) {
				message += 'File is the incorrect type.\n';
			}
			if (rejectionError.code === ErrorCode.FileTooLarge) {
				message += 'File is too large.\n';
			}
			if (rejectionError.code === ErrorCode.FileTooSmall) {
				message += 'File is too small.\n';
			}
			if (rejectionError.code === ErrorCode.TooManyFiles) {
				message += 'Too many files.\n';
			}
		}
	}

	return message;
};

export const formatBytes = (
	bytes: number,
	{ binary = true, decimals = 1 }: { binary?: boolean; decimals?: number } = {}
) => {
	if (bytes === 0) {
		return '0 bytes';
	}
	const k = binary ? 1024 : 1000;
	const units = binary ? ['bytes', 'KiB', 'MiB', 'GiB'] : ['bytes', 'KB', 'MB', 'GB'];
	const i = Math.floor(Math.log(bytes) / Math.log(k));
	const val = bytes / Math.pow(k, i);
	// emulate HALF_EVEN is overkill here; plain toFixed is usually fine
	return `${Number(val.toFixed(decimals))} ${units[i]}`;
};
